import {
  ContentsUnion,
  DocumentPackageVerificationResultInterface,
  EventEntryEnroll,
  InsuranceCompanyGroupInfoInterface,
  IPaginatorData,
  ListQueryInterface,
  PaperDocumentsListInterface,
  PaperDocumentsPackageConditionInterface,
  PaperDocumentsUserListInterface,
  PaperDocumentsVerificationInterface,
  PathRequestAdminDetailsInterface,
  PathRequestAdminListInterface,
  PathRequestAdminListQueryInterface,
  PathRequestEventAdminListItemInterface,
  PathRequestEventAttendanceBodyInterface,
  PathRequestEventListInterface,
  PathRequestEventUserListInterface,
  PathRequestEventUserListQueryInterface,
  PathRequestEventUserResultsBodyInterface,
  PathRequestExamAssignmentDetailsInterface,
  PathRequestExamTypeAssignmentBodyInterface,
  PathRequestExamTypeInterface,
  PathRequestInitRequestInterface,
  UserPathRequestDetailsInterface,
} from '@elearning-platform/api-types/interfaces';
import { AxiosProgressEvent } from 'axios';
import { stringify } from 'query-string';
import { Dispatch, SetStateAction } from 'react';

import { api } from '../api';

export const getAdminAllPathRequests: (
  params: PathRequestAdminListQueryInterface
) => Promise<PathRequestAdminListInterface> = (
  params: PathRequestAdminListQueryInterface
): Promise<PathRequestAdminListInterface> => api.get(
  `path-requests/admin-list?${stringify(params, { skipEmptyString: true })}`
);

export const getPathDetailsForAdmin: (id: number) => Promise<PathRequestAdminDetailsInterface> = (
  id: number
): Promise<PathRequestAdminDetailsInterface> => api.get(
  `/path-requests/${id}/admin-details`
);

export const getAdminPathRequestsEvents: (
  params: ListQueryInterface
) => Promise<IPaginatorData<PathRequestEventAdminListItemInterface>> = (
  params: ListQueryInterface
): Promise<IPaginatorData<PathRequestEventAdminListItemInterface>> => api.get(
  `path-requests/events/admin-list?${stringify(params, { skipEmptyString: true })}`
);

export const getUserEventDetails: (requestId: string) => Promise<PathRequestEventListInterface>
  = (requestId: string): Promise<PathRequestEventListInterface> => api.get(`v1/path-requests/${requestId}/events-user`);

export const getSelfEventDetails: (requestId: string) => Promise<PathRequestEventListInterface>
  = (requestId: string): Promise<PathRequestEventListInterface> => api.get(`v1/path-requests/${requestId}/events-self`);

export const enrollUser: (requestId: string, eventId: string) => Promise<void>
  = (requestId: string, eventId: string): Promise<void> => api.post(`v1/path-requests/${requestId}/events/${eventId}/enroll-user`);

export const enrollSelf: (requestId: string, eventId: string, data: EventEntryEnroll) => Promise<void>
  = (requestId: string, eventId: string, data: EventEntryEnroll): Promise<void> =>
    api.post(`v1/path-requests/${requestId}/events/${eventId}/enroll-self`, data);

export const disenrollUser: (requestId: string, eventId: string) => Promise<void>
  = (requestId: string, eventId: string): Promise<void> => api.delete(`v1/path-requests/${requestId}/events/${eventId}/disenroll-user`);

export const disenrollSelf: (requestId: string, eventId: string) => Promise<void>
  = (requestId: string, eventId: string): Promise<void> => api.delete(`v1/path-requests/${requestId}/events/${eventId}/disenroll-self`);

export const initUserPathRequest: (data: PathRequestInitRequestInterface) => Promise<UserPathRequestDetailsInterface>
  = (data: PathRequestInitRequestInterface): Promise<UserPathRequestDetailsInterface> => api.post('path-requests/user-init', data);

export const getUserPathRequests: () => Promise<UserPathRequestDetailsInterface[]>
  = (): Promise<UserPathRequestDetailsInterface[]> => api.get('path-requests/user-list');

export const getUserPathRequestInsuraceCompanyGroups: () => Promise<InsuranceCompanyGroupInfoInterface[]>
  = (): Promise<InsuranceCompanyGroupInfoInterface[]> => api.get('path-requests/user-insurance-groups');

export const getPathRequestPaperDocumentsList: (id: number) => Promise<PaperDocumentsUserListInterface>
  = (id: number): Promise<PaperDocumentsUserListInterface> => api.get(`/path-requests/${id}/paper-documents/list`);

export const getPathRequestPaperDocumentsListForVerification: (id: number) => Promise<PaperDocumentsListInterface>
  = (id: number): Promise<PaperDocumentsListInterface> => api.get(`/path-requests/${id}/paper-documents/verification-list`);

export const submitPaperDocumentsPackageCondition: (
  id: number, data: PaperDocumentsPackageConditionInterface
) => Promise<DocumentPackageVerificationResultInterface>
  = (
    id: number, data: PaperDocumentsPackageConditionInterface
  ): Promise<DocumentPackageVerificationResultInterface> => api.post(`/path-requests/${id}/paper-documents/package`, data);

export const verifyPaperDocuments: (
  id: number, data: PaperDocumentsVerificationInterface
) => Promise<DocumentPackageVerificationResultInterface>
  = (
    id: number, data: PaperDocumentsVerificationInterface
  ): Promise<DocumentPackageVerificationResultInterface> => api.post(`/path-requests/${id}/paper-documents/verify`, data);

export const getEventUsersList: (
  eventId: string, params: PathRequestEventUserListQueryInterface,
) => Promise<PathRequestEventUserListInterface> = (
  eventId: string, params: PathRequestEventUserListQueryInterface,
): Promise<PathRequestEventUserListInterface> =>
  api.get(`path-requests/events/${eventId}/users?${stringify(params, { skipEmptyString: true })}`);

export const markAttendance: (eventId: string, data: PathRequestEventAttendanceBodyInterface) => Promise<void> = (
  eventId: string, data: PathRequestEventAttendanceBodyInterface
): Promise<void> => api.post(`path-requests/events/${eventId}/users/attendance`, data);

export const importResultsForGroup: (
  eventId: string, file: File, setProgress: Dispatch<SetStateAction<number | undefined>>
) => Promise<void> = (
  eventId: string, file: File, setProgress: Dispatch<SetStateAction<number | undefined>>
): Promise<void> => {
  const formData: FormData = new FormData();
  formData.append('file', file);

  return api.post(
    `path-requests/events/${eventId}/import`,
    formData,
    {
      customEnv: { notEncryptedRequest: true },
      onUploadProgress: (event: AxiosProgressEvent): void => {
        if (event.total) {
          const percent: number = Math.round((event.loaded * 100) / event.total);
          setProgress(percent);
        }
      },
    }
  );
};

export const saveResults: (eventId: string, userId: string, payload: PathRequestEventUserResultsBodyInterface) => Promise<void> = (
  eventId: string, userId: string, payload: PathRequestEventUserResultsBodyInterface
): Promise<void> => api.post(`path-requests/events/${eventId}/users/${userId}/results`, payload);

export const confirmFileUpload: (eventId: string, userId: string, partId: number, documentId: number) => Promise<void> = (
  eventId: string, userId: string, partId: number, documentId: number,
): Promise<void> => api.post(
  `path-requests/events/${eventId}/users/${userId}/certificate/${partId}/confirm`, { documentId },
);

export const downloadDocumentsFromPathRequest: (requestId: number) => Promise<void> = (
  requestId: number
): Promise<void> => api.get(
  `path-requests/${requestId}/documents/download`, { responseType: 'blob' }
);

export const getEventsRequiringCompletion: (requestId: number) => Promise<ContentsUnion[]> = (
  requestId: number,
): Promise<ContentsUnion[]> =>
  api.get(`path-requests/learning/${requestId}/list`);

export const getExamAssignmentDetails: (requestId: number) => Promise<PathRequestExamAssignmentDetailsInterface> = (
  requestId: number
): Promise<PathRequestExamAssignmentDetailsInterface> => api.get(
  `path-requests/${requestId}/exam-assignment-details`
);

export const getExamTypes: (requestId: number) => Promise<PathRequestExamTypeInterface[]> = (
  requestId: number
): Promise<PathRequestExamTypeInterface[]> => api.get(
  `path-requests/${requestId}/exam-types`
);

export const exemptUserFromExam: (requestId: number) => Promise<void> = (
  requestId: number
): Promise<void> => api.post(
  `path-requests/${requestId}/exam-exemption`
);

export const assignExamTypeToPathRequest: (requestId: number, payload: PathRequestExamTypeAssignmentBodyInterface) => Promise<void> = (
  requestId: number, payload: PathRequestExamTypeAssignmentBodyInterface
): Promise<void> => api.put(
  `path-requests/${requestId}/exam-type-assignment`, payload
);
